import axios from 'axios';
import {
  getSecurityTier,
  isVaultUnlocked,
  getVaultVersion,
  vaultSaveProject,
  vaultLoadProject,
  vaultSaveProjectV2,
  vaultLoadProjectV2,
} from './vaultService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** List all projects for the current user */
export async function listProjects() {
  const { data } = await axios.get(`${API}/projects`);
  return data;
}

/** Create a new project */
export async function createProject(projectData) {
  const { data } = await axios.post(`${API}/projects`, projectData);
  return data;
}

/** Load a single project by ID */
export async function loadProject(projectId) {
  // Standard cloud load
  const { data } = await axios.get(`${API}/projects/${projectId}`);

  // If vault is active and unlocked, also load and merge decrypted vault data
  if (getSecurityTier() === 'vault' && isVaultUnlocked()) {
    try {
      const version = getVaultVersion();
      const vaultData = version >= 2
        ? await vaultLoadProjectV2(projectId)
        : await vaultLoadProject(projectId);

      if (vaultData) {
        // Vault data overrides cloud data for encrypted fields
        return { ...data, ...vaultData, id: data.id, user_id: data.user_id };
      }
    } catch (e) {
      console.warn('Vault load failed, using cloud data:', e);
    }
  }

  return data;
}

/** Save/update a project — accepts the full project data object */
export async function saveProject(projectId, projectData) {
  const { data } = await axios.put(`${API}/projects/${projectId}`, projectData);

  // If vault is active and unlocked, also save encrypted copy
  if (getSecurityTier() === 'vault' && isVaultUnlocked()) {
    try {
      const version = getVaultVersion();
      if (version >= 2) {
        await vaultSaveProjectV2(projectId, projectData);
      } else {
        await vaultSaveProject(projectId, projectData);
      }
    } catch (e) {
      console.warn('Vault save failed (cloud save succeeded):', e);
    }
  }

  return data;
}

/** Delete a project */
export async function deleteProject(projectId) {
  const { data } = await axios.delete(`${API}/projects/${projectId}`);
  return data;
}

/** Generate AI poster for a project */
export async function generatePoster(projectId) {
  const { data } = await axios.post(`${API}/projects/${projectId}/generate-poster`, {});
  return data;
}

/** Upload a custom thumbnail */
export async function uploadThumbnail(projectId, thumbnailData) {
  const { data } = await axios.post(`${API}/projects/${projectId}/upload-thumbnail`, thumbnailData);
  return data;
}

/** Get generation queue status */
export async function getGenerationStatus(projectId) {
  const { data } = await axios.get(`${API}/projects/${projectId}/generation-status`);
  return data;
}

/** Stop all generation for a project */
export async function stopGeneration(projectId) {
  const { data } = await axios.post(`${API}/projects/${projectId}/stop-generation`, {});
  return data;
}
